import { useEffect, useMemo, useState } from "react";
import { guid } from "odata-query";
import { MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  ENTITY_NAMES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import {
  IVerticalTabProps,
  IWorkOrderTemplateDetailsResponse,
  IWorkOrderTemplateListItemResponse,
  PlatformModules,
} from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import {
  useOrderTemplates,
  usePermissions,
  useWorkOrderTemplate,
  useWorkOrderTemplates,
} from "@mibuildweb/hooks";
import { ConfigWorkOrderTemplateCard } from "./components/ConfigWorkOrderTemplateCard";
import { MODULES_CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { IWorkOrderTemplateData } from "./ConfigWorkOrderTemplates.types";

export function ConfigWorkOrderTemplatesList({
  module,
}: Readonly<{ module: PlatformModules }>) {
  const [search, setSearch] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [allWorkOrderTemplates, setAllWorkOrderTemplates] = useState<
    IWorkOrderTemplateListItemResponse[]
  >([]);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [selectedWorkOrderTemplate, setSelectedWorkOrderTemplate] = useState<
    | IWorkOrderTemplateDetailsResponse
    | IWorkOrderTemplateListItemResponse
    | null
  >(null);

  const { navigate } = useRouter();
  const { hasPermission } = usePermissions();

  const debouncedSearch = useDebounce(search);

  const {
    isLoading,
    setWorkOrderTemplates,
    workOrderTemplates,
    recentWorkOrderTemplates,
    setRecentWorkOrderTemplates,
    getWorkOrderTemplates,
    getRecentWorkOrderTemplates,
    filter,
    setFilter,
  } = useWorkOrderTemplates({ debouncedSearch, module });

  const { deleteWorkOrderTemplate, updateWorkOrderTemplate } =
    useWorkOrderTemplate({ module });

  const { getOrderTemplates, setOrderTemplates, orderTemplates } =
    useOrderTemplates({ module });

  const canCreateWorkOrderTemplate =
    hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.CREATE) &&
    orderTemplates.length > 0;
  const canDeleteWorkOrderTemplate = hasPermission(
    PERMISSIONS.WORK_ORDER_TEMPLATE.DELETE
  );
  const canUpdateWorkOrderTemplate = hasPermission(
    PERMISSIONS.WORK_ORDER_TEMPLATE.UPDATE
  );

  useEffect(() => {
    const fetchOrderTemplates = async () => {
      const response = await getOrderTemplates();
      if (response?.data) {
        setOrderTemplates(response.data);
      }
    };
    // reference of all work order templates necessary for filtering tabs
    const fetchAllWorkOrderTemplates = async () => {
      const response = await getWorkOrderTemplates();
      if (response?.data) {
        setAllWorkOrderTemplates(response.data);
      }
    };
    fetchOrderTemplates();
    fetchAllWorkOrderTemplates();
    setTriggerRefresh((prev) => !prev);
  }, [module]);

  const tabs = useMemo(() => {
    const tabs = [];
    const filteredOrderTemplates = orderTemplates.filter((ot) =>
      allWorkOrderTemplates.some((wot) => wot.orderTemplateId === ot.id)
    );
    const orderTemplateTabs = filteredOrderTemplates.map((orderTemplate) => ({
      label: orderTemplate.name,
      value: orderTemplate.id,
      filter: { orderTemplateId: { eq: guid(orderTemplate.id) } },
    }));
    tabs.push({ label: "All", value: "All", filter: {} });
    return tabs.concat(orderTemplateTabs);
  }, [orderTemplates, allWorkOrderTemplates]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleOpenDeleteModal = (item: IWorkOrderTemplateListItemResponse) => {
    setSelectedWorkOrderTemplate(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = async () => {
    if (selectedWorkOrderTemplate) {
      setOpenDeleteModal(false);
      await deleteWorkOrderTemplate(selectedWorkOrderTemplate);
      setTriggerRefresh((prev) => !prev);
    }
  };

  const handleUpdateWorkOrderTemplate = async (
    data: Partial<IWorkOrderTemplateData>,
    name?: string
  ) => {
    await updateWorkOrderTemplate(data, name);
    setTriggerRefresh((prev) => !prev);
  };

  const navigateToAddWorkOrderTemplate = () => {
    navigate(
      `${
        MODULES_CONFIG_PATHS[module][CONFIG_PAGES.WORK_ORDER_TEMPLATES]
      }/add-work-order-type`
    );
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IWorkOrderTemplateDetailsResponse[]> | null
  ) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const refreshData = useMemo(
    () => ({ triggerRefresh, filter }),
    [triggerRefresh, filter]
  );

  return (
    <ConfigPageLayout
      title={ENTITY_NAMES.WORK_ORDER_TEMPLATES}
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      titleActionComponent={
        canCreateWorkOrderTemplate && (
          <AddButton
            label={`Add ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`}
            onClick={navigateToAddWorkOrderTemplate}
          />
        )
      }
      loading={isLoading}
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={tabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={workOrderTemplates ?? []}
          dataProvider={getWorkOrderTemplates}
          loading={isLoading}
          setData={setWorkOrderTemplates}
          CardComponent={ConfigWorkOrderTemplateCard}
          cardProps={{
            handleOpenDeleteModal,
            handleUpdateWorkOrderTemplate,
            canUpdateWorkOrderTemplate,
            canDeleteWorkOrderTemplate,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          recentData={recentWorkOrderTemplates}
          setRecentData={setRecentWorkOrderTemplates}
          recentDataProvider={getRecentWorkOrderTemplates}
          entityName={ENTITY_NAMES.WORK_ORDER_TEMPLATES}
          emptyListProps={{
            text: `No ${ENTITY_NAMES.WORK_ORDER_TEMPLATES} found`,
          }}
          refresh={refreshData}
        />
      </VerticalTabsLayout>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedWorkOrderTemplate?.name}. Are you sure you would like to continue?`}
      />
    </ConfigPageLayout>
  );
}
